// src/components/PrivacyPolicyModal/content/en.tsx
import { FC } from 'react';

const EnContent: FC = () => (
  <>
    <h2>PRIVACY POLICY - THE PATCH</h2>
    <p>
      <strong>Last updated: 27/01/2026</strong>
    </p>
    <p>
      This Privacy Policy is intended to explain, clearly and transparently, how <strong>The Patch</strong> (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) collects, uses, stores, and protects the
      personal data of users (&quot;you&quot;) in connection with the use of The Patch platform (the &quot;Services&quot;).
    </p>
    <p>By using The Patch, you agree to the practices described below.</p>
    <p>If you do not agree with this policy, please refrain from using our Services.</p>
    {/* TODO(brand): keep prepwise.fr email temporarily; update after mailbox migration. */}
    <p>
      For any questions: <strong>contact@prepwise.fr</strong>
    </p>

    <hr />

    <h3>1. Key Points Summary</h3>
    <ul>
      <li>We collect only the data necessary for the operation and improvement of The Patch</li>
      <li>
        We process <strong>no sensitive personal data</strong> within the meaning of the GDPR
      </li>
      <li>You retain full control over your data (access, deletion, objection)</li>
      <li>
        User content is used to <strong>support training</strong>, <strong>provide relevant opportunities</strong>, and <strong>improve the service</strong>
      </li>
    </ul>

    <hr />

    <h3>2. What Data Do We Collect?</h3>
    <h4>2.1 Data You Provide Directly</h4>
    <p>When using The Patch, you may provide us with the following data:</p>
    <ul>
      <li>Identity data: first name, last name, email address</li>
      <li>Account data: username, password (encrypted)</li>
      <li>Application-related data: résumés (CVs), cover letters, targeted job descriptions</li>
      <li>Training data: written responses, oral responses (audio files), generated feedback and scores</li>
      <li>Platform usage history</li>
    </ul>
    <p>These data are necessary to deliver the Services offered.</p>

    <h4>2.2 Data Collected Automatically</h4>
    <p>When you browse the platform, certain technical data may be collected automatically, including:</p>
    <ul>
      <li>IP address</li>
      <li>browser type and device type</li>
      <li>operating system</li>
      <li>navigation and usage data</li>
    </ul>
    <p>These data are used for security purposes, service operation, and service improvement.</p>

    <hr />

    <h3>3. Purposes of Data Processing</h3>
    <p>We process your personal data in order to:</p>
    <ul>
      <li>create and manage your user account</li>
      <li>analyze your résumé and cover letters</li>
      <li>generate personalized résumés</li>
      <li>generate personalized cover letters</li>
      <li>simulate job interviews and provide feedback</li>
      <li>suggest job opportunities relevant to your profile</li>
      <li>enable, where applicable, direct job applications via The Patch</li>
      <li>improve the quality, relevance, and performance of the Services</li>
      <li>ensure security and prevent fraudulent use</li>
      <li>comply with our legal obligations</li>
    </ul>
    <p>
      <strong>Important:</strong> The Patch is a training and preparation tool. The analyses, scores, and recommendations provided{' '}
      <strong>do not constitute recruitment decisions and do not guarantee employment</strong>.
    </p>

    <hr />

    <h3>4. Legal Bases for Processing (GDPR)</h3>
    <p>In accordance with the GDPR, our processing activities are based on:</p>
    <ul>
      <li>
        your <strong>consent</strong> (e.g. account creation, data submission)
      </li>
      <li>
        the <strong>performance of a contract</strong> (delivery of The Patch services)
      </li>
      <li>
        our <strong>legitimate interest</strong> (service improvement, security)
      </li>
      <li>
        compliance with <strong>legal obligations</strong>
      </li>
    </ul>
    <p>You may withdraw your consent at any time.</p>

    <hr />

    <h3>5. Data Sharing with Third Parties</h3>
    <p>Your data may be shared only with:</p>
    <ul>
      <li>technical service providers (hosting, cloud services, AI, analytics)</li>
      <li>partners strictly necessary for the operation of the Services</li>
      <li>administrative or judicial authorities where legally required</li>
    </ul>
    <p>All partners are bound by strict contractual obligations regarding confidentiality and data security.</p>

    <hr />

    <h3>6. Artificial Intelligence, Models, and Service Improvement</h3>
    <p>User-provided content (CVs, responses, feedback) may be used in an:</p>
    <ul>
      <li>
        <strong>anonymized or pseudonymized</strong> manner
      </li>
      <li>for the purpose of improving algorithms and service quality</li>
    </ul>
    <p>No personally identifiable data is used to train models accessible to third parties.</p>

    <hr />

    <h3>7. Cookies and Similar Technologies</h3>
    <p>The Patch uses cookies necessary for the proper functioning of the platform and, where applicable, analytical cookies.</p>
    <p>You may manage your preferences via your browser settings or through a dedicated cookie consent banner.</p>

    <hr />

    <h3>8. Third-Party Authentication</h3>
    <p>If you choose to register via a third-party service (Google, LinkedIn, etc.), we collect only the information strictly necessary for authentication.</p>
    <p>We do not control the data practices of these platforms and invite you to review their respective privacy policies.</p>

    <hr />

    <h3>9. Data Retention Periods</h3>
    <ul>
      <li>Active account data: retained as long as your account remains active</li>
      <li>
        Deleted account: data deleted or anonymized within a maximum period of <strong>6 months</strong>
      </li>
      <li>
        Prolonged inactivity: data deleted after <strong>3 years without login</strong>, unless otherwise instructed
      </li>
    </ul>
    <p>Certain data may be retained for longer periods where required by law.</p>

    <hr />

    <h3>10. Data Relating to Minors</h3>
    <p>
      The Patch is intended exclusively for individuals aged <strong>18 years or older</strong>.
    </p>
    <p>We do not knowingly collect personal data relating to minors.</p>
    <p>If such data are identified, they will be deleted without delay.</p>

    <hr />

    <h3>11. Your Rights</h3>
    <p>In accordance with the GDPR, you have the following rights:</p>
    <ul>
      <li>right of access</li>
      <li>right to rectification</li>
      <li>right to erasure</li>
      <li>right to restriction of processing</li>
      <li>right to object</li>
      <li>right to data portability</li>
    </ul>
    {/* TODO(brand): keep prepwise.fr email temporarily; update after mailbox migration. */}
    <p>
      To exercise your rights, please contact: <strong>contact@prepwise.fr</strong>
    </p>
    <p>You also have the right to lodge a complaint with the competent supervisory authority (e.g. CNIL, Swiss Federal Data Protection Authority).</p>

    <hr />

    <h3>12. Do-Not-Track Signals</h3>
    <p>At present, The Patch does not respond to &quot;Do-Not-Track&quot; signals, as no universally recognized standard exists.</p>

    <hr />

    <h3>13. Updates to This Policy</h3>
    <p>This Privacy Policy may be updated at any time.</p>
    <p>In the event of a material change, you will be informed by email or via the platform.</p>

    <hr />

    <h3>14. Data Controller and Contact</h3>
    <p>
      <strong>Data Controller:</strong> Ilyas Redjem
    </p>
    {/* TODO(brand): keep prepwise.fr email temporarily; update after mailbox migration. */}
    <p>
      Email: <strong>ilyas@prepwise.fr</strong>
    </p>
  </>
);

export default EnContent;
